import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);

// Locale-independent mapping: day index (0=Sunday…6=Saturday) → DB enum value
const DB_DAY_NAMES = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];

// Spanish names for display messages in findNextOpening
const ES_DAY_NAMES = [
  "domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado",
];

// Normalize Prisma enum key → IANA timezone string
// e.g. "America_Bogota" → "America/Bogota"
// Only replaces the first underscore (region/city separator);
// underscores within city names (e.g. New_York, Sao_Paulo) are preserved.
function normalizeTz(tz) {
  if (!tz) return "America/Bogota";
  // Already IANA format (has a slash) — return as-is
  if (tz.includes("/")) return tz;
  return tz.replace("_", "/");
}

// Returns true when the value is a valid HH:mm string
const TIME_RE = /^\d{2}:\d{2}$/;

function parseScheduleTime(dateStr, timeStr, tz) {
  return dayjs.tz(`${dateStr}T${timeStr}`, tz);
}

export function isSedeOpen(sede) {
  // Accept both `sede.schedule` (legacy) and `sede.schedules` (Prisma relation)
  const schedule = Array.isArray(sede?.schedule)
    ? sede.schedule
    : Array.isArray(sede?.schedules)
    ? sede.schedules
    : null;

  if (!sede || !schedule || schedule.length === 0) {
    return { open: false, message: "Horario no disponible" };
  }

  const tz = normalizeTz(sede.timezone);

  let now;
  try {
    now = dayjs().tz(tz);
  } catch {
    now = dayjs();
  }

  // ── Check if yesterday's midnight-crossing schedule still covers now ──────
  const yesterdayKey = DB_DAY_NAMES[now.subtract(1, "day").day()];
  const yesterdaySched = schedule.find((d) => d.day === yesterdayKey && !d.closed);
  if (yesterdaySched && TIME_RE.test(yesterdaySched.open) && TIME_RE.test(yesterdaySched.close)) {
    try {
      const yDate   = now.subtract(1, "day").format("YYYY-MM-DD");
      const yOpen   = parseScheduleTime(yDate, yesterdaySched.open,  tz);
      const yClose  = parseScheduleTime(yDate, yesterdaySched.close, tz);
      // Only relevant when close ≤ open (midnight-crossing from yesterday)
      if (!yClose.isAfter(yOpen)) {
        const yCloseAdj = yClose.add(1, "day");
        if (!now.isBefore(yOpen) && now.isBefore(yCloseAdj)) {
          return { open: true, message: `Cierra a las ${yesterdaySched.close}` };
        }
      }
    } catch {
      // ignore, fall through to today's check
    }
  }

  // ── Today's schedule ──────────────────────────────────────────────────────
  const todayKey = DB_DAY_NAMES[now.day()];
  const todaySchedule = schedule.find((d) => d.day === todayKey && !d.closed);

  if (!todaySchedule) {
    return findNextOpening(now, schedule);
  }

  if (!TIME_RE.test(todaySchedule.open) || !TIME_RE.test(todaySchedule.close)) {
    return findNextOpening(now, schedule);
  }

  let openTime, closeTime;
  try {
    const dateStr = now.format("YYYY-MM-DD");
    openTime  = parseScheduleTime(dateStr, todaySchedule.open,  tz);
    closeTime = parseScheduleTime(dateStr, todaySchedule.close, tz);
  } catch {
    return { open: false, message: "Horario inválido" };
  }

  // Midnight-crossing: if close ≤ open (e.g. open 22:00, close 02:00),
  // the closing time belongs to the next calendar day.
  if (!closeTime.isAfter(openTime)) {
    closeTime = closeTime.add(1, "day");
  }

  // At or after open AND before close → currently open
  if (!now.isBefore(openTime) && now.isBefore(closeTime)) {
    return { open: true, message: `Cierra a las ${todaySchedule.close}` };
  }

  // Before open time → will open later today
  if (now.isBefore(openTime)) {
    return { open: false, message: `Abre hoy a las ${todaySchedule.open}` };
  }

  // Past close time → find next opening day
  return findNextOpening(now, schedule);
}

function findNextOpening(now, schedule) {
  for (let i = 1; i <= 7; i++) {
    const nextDay   = now.add(i, "day");
    const nextKey   = DB_DAY_NAMES[nextDay.day()];
    const nextSched = schedule.find((d) => d.day === nextKey && !d.closed);
    if (nextSched) {
      const dayLabel = i === 1 ? "mañana" : ES_DAY_NAMES[nextDay.day()];
      return {
        open: false,
        message: `Abre ${dayLabel} a las ${nextSched.open}`,
      };
    }
  }
  return { open: false, message: "Cerrado temporalmente" };
}
